import { useCreateCertificateAgreement } from '@/core/domain/enterprise/AgreementCertificate/AgreementCertificate.hooks';
import { BankAccount } from '@/core/domain/enterprise/BankAccounts/BankAccounts.types';
import {
  useBankAccountSettings,
  useCreateBankAccountSettings,
  useUpdateBankAccountSettings,
} from '@/core/domain/enterprise/BankAccountsSettings/BankAccountsSettings.hooks';
import {
  BankAccountSettingsAgreement,
  BankAccountSettingsFormValues,
  BankAccountSettingsUpdateRequest,
} from '@/core/domain/enterprise/BankAccountsSettings/BankAccountsSettings.types';
import { FormInstance, message, notification } from 'antd';
import { useQueryClient } from 'react-query';

interface Props {
  setTabErrors: React.Dispatch<React.SetStateAction<string[]>>;
  form: FormInstance<BankAccountSettingsFormValues>;
  bankAccount?: BankAccount;
  bankAccountId?: number;
}

export default function useBankAccountConfigSubmit({
  setTabErrors,
  form,
  bankAccount,
  bankAccountId,
}: Props) {
  const queryClient = useQueryClient();
  const bankAccountSettings = useBankAccountSettings(bankAccountId);
  const bankAccountSettingsUpdateMutation = useUpdateBankAccountSettings();
  const bankAccountSettingsCreateMutation = useCreateBankAccountSettings();
  const certificateCreateMutation = useCreateCertificateAgreement();

  async function handleSubmit() {
    try {
      setTabErrors([]);
      const values = await form.validateFields();

      if (!bankAccount?.companyId) {
        return notification.error({
          message: 'Alerta!',
          description:
            'Para realizar a configuração da conta bancária, necessário informar a empresa na aba de dados gerais',
        });
      }

      if (bankAccount && bankAccount?.companyId && bankAccountId) {
        const bankAccountSettingsAgreementObj: BankAccountSettingsAgreement = {
          agreementApiId: values.general?.agreementApiId,
          agreementApiKey: values.general?.agreementApiKey,
          agreementApiSecret: values.general?.agreementApiSecret,
          agreementWebServiceType: values.general.agreementWebServiceType,
          agreementBankOurNumber:
            typeof values.general?.agreementBankOurNumber === 'number'
              ? Boolean(values.general?.agreementBankOurNumber)
              : undefined,
          agreementCnabPattern: values.general?.agreementCnabPattern,
          agreementContractNumber: values.general?.agreementContractNumber,
          agreementDescription: values.general?.agreementDescription,
          agreementInstantRegister:
            typeof values.general?.agreementInstantRegister === 'number'
              ? Boolean(values.general?.agreementInstantRegister)
              : undefined,
          agreementLayoutFileVersion:
            values.general?.agreementLayoutFileVersion,
          agreementNumber: values.general?.agreementNumber,
          agreementRemittanceDensity:
            values.general?.agreementRemittanceDensity,
          agreementRemittanceNumber: values.general?.agreementRemittanceNumber,
          agreementRestartDaily:
            typeof values.general?.agreementRestartDaily === 'number'
              ? Boolean(values.general?.agreementRestartDaily)
              : undefined,
          agreementSpecies: values.general?.agreementSpecies,
          agreementStation: values.general?.agreementStation,
          agreementWallet: values.general?.agreementWallet,
          agreementWalletCode: values.general?.agreementWalletCode,
          agreementWsConsult:
            typeof values.general?.agreementWsConsult === 'number'
              ? Boolean(values.general?.agreementWsConsult)
              : undefined,
          agreementHybridEmission:
            typeof values.general?.agreementHybridEmission === 'number'
              ? Boolean(values.general?.agreementHybridEmission)
              : undefined,

          agreementCurrentOurNumber: values.general?.agreementCurrentOurNumber,
          agreementAilosPassword: values.general?.agreementAilosPassword,
          agreementSandboxEnvironment:
            typeof values.general?.agreementSandboxEnvironment === 'number'
              ? Boolean(values.general?.agreementSandboxEnvironment)
              : undefined,
        };

        const bankAccountSettingsDiscountObj = {
          titleDiscountCode: values.discount?.titleDiscountCode,
          titleDiscountCode2: values.discount?.titleDiscountCode2,
          titleDiscountCode3: values.discount?.titleDiscountCode3,
          titleDiscountRateValue:
            values.discount?.titleDiscountRateValue?.toLocaleString(),
          titleDiscountRateValue2:
            values.discount?.titleDiscountRateValue2?.toLocaleString(),
          titleDiscountRateValue3:
            values.discount?.titleDiscountRateValue3?.toLocaleString(),
          titleDiscountValue:
            values.discount?.titleDiscountValue?.toLocaleString(),

          titleDiscountDays: values.discount?.titleDiscountDays,
          titleDiscountDays2: values.discount?.titleDiscountDays2,
          titleDiscountDays3: values.discount?.titleDiscountDays3,
        };

        let titleMessageFields = {};

        values.messages?.forEach((message, i) => {
          titleMessageFields = {
            ...titleMessageFields,
            [`titleMessage${String(i + 1).padStart(2, '0')}`]:
              message.titleMessage,
          };
        });

        const obj: BankAccountSettingsUpdateRequest = {
          bankAccountAgreement: bankAccountSettingsAgreementObj,
          bankAccountId,

          bankFee: values?.general.bankFee,
          hasBankFee: values?.general.hasBankFee,
          planAccountId: values?.general.planAccountId,

          titleCodeReference: values?.general?.titleCodeReference,

          titleAcceptance: values?.general?.titleAcceptance,
          titleDocumentSpecies: values?.general?.titleDocumentSpecies,
          titleVariationWallet: values.general?.titleVariationWallet,

          titleInterestCode: values?.interest?.titleInterestCode,
          titleInterestDays: values?.interest?.titleInterestDays,
          titleInterestValue:
            values?.interest?.titleInterestValue?.toLocaleString('pt-br', {
              minimumFractionDigits: bankAccount?.bank.code === '246' ? 4 : 2,
              maximumFractionDigits: 2,
            }),

          titleIdentificationTypePayment:
            values.general.titleIdentificationTypePayment,

          ...bankAccountSettingsDiscountObj,

          titleFineCode: values?.fine?.titleFineCode,
          titleFineDays: values?.fine?.titleFineDays,
          titleFineRateValue:
            values?.fine?.titleFineRateValue?.toLocaleString(),

          titleProtestCode: values?.protest?.titleProtestCode,
          titleProtestTerm: values?.protest?.titleProtestTerm,

          ...titleMessageFields,

          paymentDateType: values.general.paymentDateType,
        };

        if (bankAccountSettings?.data?.id) {
          await bankAccountSettingsUpdateMutation.mutateAsync(obj);

          const certificateFile = values.certificate.certificateFile;

          if (
            Array.isArray(certificateFile) &&
            !!certificateFile[0].originFileObj
          ) {
            if (
              bankAccountSettings.data?.bankAccountAgreement &&
              !bankAccountSettings.data?.certificate
            ) {
              await certificateCreateMutation.mutateAsync({
                bankAccountId: String(bankAccountId),
                certificateAlias: values.certificate.certificateAlias,
                certificateEmail: values.certificate.certificateEmail,
                certificatePassword: values.certificate.certificatePassword,
                file: certificateFile[0].originFileObj,
              });
            }
          } else {
            message.info({ content: 'Informe um arquivo válido' });
          }
        } else {
          await bankAccountSettingsCreateMutation.mutateAsync(obj);
        }

        await queryClient.invalidateQueries([
          'bankAccountSettings',
          bankAccountId,
        ]);
      }
    } catch (err: unknown) {
      const error = err as { errorFields?: { name: string[] }[] };

      if (error.errorFields?.length) {
        setTabErrors(
          error.errorFields.reduce((acc, curr) => {
            const name = curr.name[0];

            if (!acc.includes(name)) {
              return [...acc, name];
            }

            return acc;
          }, [] as string[]),
        );

        message.error('Preencha os campos obrigatórios');
      }

      return false;
    }
  }

  return {
    isLoading:
      bankAccountSettingsUpdateMutation.isLoading ||
      bankAccountSettingsCreateMutation.isLoading ||
      certificateCreateMutation.isLoading,

    handleSubmit,
  };
}
